import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import moment from 'moment';
import classNames from 'classnames';
import { Skeleton } from '@mui/material';
import css from './AnalysisTab.css';
import sharedCss from './sharedTabStyles.css';
import MetricField from '../creativeCardData/metricField/MetricField';
import { SHORTEN_LARGE_NUMBER_CONFIG } from '../creativeCardData/metricDisplayUtils';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import CreativeChart from './CreativeChart';
import { LINE_CHART, COLUMN_CHART } from '../../../utils/dashboardConsts';
import { DIMENSION_OPTIONS } from '../../consts';

const DATE_FORMAT = 'D MMM YYYY';
const ABOUT_CREATIVE_TEXT = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ANALYSIS.ABOUT_CREATIVE';
const CREATIVE_PERFORMANCE_TITLE = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ANALYSIS.CREATIVE_PERFORMANCE';
const CREATIVE_BREAKDOWN_TITLE = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ANALYSIS.CREATIVE_BREAKDOWN_BY';

const convertToChartOption = (item, isCohort) => ({
    ...item,
    name: item.value,
    display_name: item.label,
    isCohort,
});

const hasValue = value => Boolean(value);

const AnalysisTab = ({
    metrics = {},
    cohorts = {},
    selectedMetrics = [],
    selectedCohorts = [],
    dateRange,
    isLoadingMetrics = false,
    isLoadingCohorts = false,
    chartData = { performance: {}, breakdown: {} },
}) => {
    const [isHovering, setIsHovering] = useState(false);
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const getItemData = isCohort => (isCohort ? cohorts : metrics);

    const allOptions = useMemo(
        () => [
            ...selectedMetrics.map(item => convertToChartOption(item, false)),
            ...selectedCohorts.map(item => convertToChartOption(item, true)),
        ],
        [selectedMetrics, selectedCohorts]
    );

    const metricsDropdownOptions = useMemo(() => [{ options: allOptions }], [allOptions]);

    const combinedItems = useMemo(() => {
        return [...allOptions].sort((a, b) => {
            const aData = getItemData(a.isCohort);
            const bData = getItemData(b.isCohort);
            const aHasValue = hasValue(aData[a.value]);
            const bHasValue = hasValue(bData[b.value]);

            if (aHasValue && !bHasValue) return -1;
            if (!aHasValue && bHasValue) return 1;
            return 0;
        });
    }, [allOptions, metrics, cohorts]);

    const isItemLoading = item =>
        item.isCohort ? isLoadingCohorts || cohorts[item.value] === undefined : isLoadingMetrics;

    const dateRangeLabel = dateRange
        ? `${moment(dateRange.startDate).format(DATE_FORMAT)} - ${moment(dateRange.endDate).format(DATE_FORMAT)}`
        : '';

    return (
        <div className={sharedCss.tabContainer}>
            {dateRange && (
                <div className={sharedCss.tabTitle}>
                    <Translate id={ABOUT_CREATIVE_TEXT} />
                    <span className={css.dateRange}>{` - ${dateRangeLabel}`}</span>
                </div>
            )}
            <div
                className={classNames(css.metricsGrid, { [css.metricsGridHover]: isHovering })}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {combinedItems.map(item => {
                    const data = getItemData(item.isCohort);
                    return isItemLoading(item) ? (
                        <div key={item.value} className={css.metricCard}>
                            <div className={css.metricLabel}>{item.label}</div>
                            <Skeleton className={css.metricSkeleton} />
                        </div>
                    ) : (
                        <MetricField
                            key={item.value}
                            label={item.label}
                            value={item.value}
                            data={data}
                            classNames={{
                                field: css.metricCard,
                                label: css.metricLabel,
                                value: css.metricValue,
                            }}
                            cohort={item.isCohort}
                            shortenConfig={SHORTEN_LARGE_NUMBER_CONFIG}
                        />
                    );
                })}
            </div>
            <CreativeChart
                title={CREATIVE_PERFORMANCE_TITLE}
                dropdowns={metricsDropdownOptions}
                data={chartData.performance.data}
                loading={chartData.performance.loading}
                chartType={LINE_CHART}
            />
            <CreativeChart
                title={CREATIVE_BREAKDOWN_TITLE}
                dropdowns={metricsDropdownOptions}
                data={chartData.breakdown.data}
                loading={chartData.breakdown.loading}
                chartType={COLUMN_CHART}
                tabs={DIMENSION_OPTIONS}
                selectedTab={chartData.breakdown.selectedBreakdown}
                onTabChange={chartData.breakdown.setSelectedBreakdown}
            />
        </div>
    );
};

AnalysisTab.propTypes = {
    metrics: PropTypes.object,
    cohorts: PropTypes.object,
    selectedMetrics: PropTypes.arrayOf(AutoCompleteOptionType),
    selectedCohorts: PropTypes.arrayOf(AutoCompleteOptionType),
    dateRange: PropTypes.object.isRequired,
    isLoadingMetrics: PropTypes.bool,
    isLoadingCohorts: PropTypes.bool,
    chartData: PropTypes.shape({
        performance: PropTypes.shape({
            data: PropTypes.array,
            loading: PropTypes.bool,
        }),
        breakdown: PropTypes.shape({
            data: PropTypes.array,
            loading: PropTypes.bool,
            selectedBreakdown: PropTypes.object,
            setSelectedBreakdown: PropTypes.func,
        }),
    }),
};

export default AnalysisTab;
